import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseModal, Button, LoadingButton, Input } from '@airclou/ui'
import { Lock } from 'lucide-react'

interface ConfirmPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (password: string) => Promise<void>
  error?: string | null
}

export function ConfirmPasswordModal({ isOpen, onClose, onConfirm, error }: ConfirmPasswordModalProps) {
  const { t } = useTranslation('dashboard')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setPassword('')
      setIsLoading(false)
    }
  }, [isOpen])

  const handleConfirm = useCallback(async () => {
    if (!password.trim() || isLoading) return
    setIsLoading(true)
    try {
      await onConfirm(password)
    } finally {
      setIsLoading(false)
    }
  }, [password, isLoading, onConfirm])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }, [handleConfirm])

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <Button variant="outline" onClick={onClose} disabled={isLoading}>
        {t('common.cancel')}
      </Button>
      <LoadingButton
        onClick={handleConfirm}
        loading={isLoading}
        disabled={!password.trim()}
      >
        {t('common.confirm')}
      </LoadingButton>
    </div>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/15 dark:bg-primary/25 rounded-full flex items-center justify-center flex-shrink-0">
            <Lock className="h-4 w-4 text-primary" />
          </div>
          <span>{t('confirmPassword.title')}</span>
        </div>
      }
      size="sm"
      footer={footer}
      contentClassName="px-6 py-4"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('confirmPassword.description')}
        </p>
        <div onKeyDown={handleKeyDown}>
          <Input
            id="confirm-password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={t('confirmPassword.placeholder')}
            disabled={isLoading}
          />
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    </BaseModal>
  )
}
